import { Center, Loader, useMantineColorScheme } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import {
  getColorSchemeOverride,
  getHeightOverride,
  getWidthOverride,
} from "../../../shared/utils/embed-detection";
import type {
  NormalizedWidgetFeatures,
  WidgetAppearance,
  WidgetConfig,
  WidgetFeatures,
} from "../lib/config-schema";
import { normalizeFeatures } from "../lib/config-schema";
import { useConfig } from "../utils/config-loader";

interface WidgetConfigContextType {
  config: WidgetConfig;
  updateConfig: (updates: Partial<WidgetConfig>) => void;
  updateFeatures: (updates: Partial<WidgetFeatures>) => void;
  updateAppearance: (updates: Partial<WidgetAppearance>) => void;
  toggleFeature: (feature: "portfolio" | "rewards" | "swap" | "defi" | "discover") => void;
  resetConfig: () => void;
  features: NormalizedWidgetFeatures;
  appearance: WidgetAppearance;
}

const WidgetConfigContext = createContext<WidgetConfigContextType | undefined>(undefined);

export const WidgetConfigProvider = ({ children }: { children: ReactNode }) => {
  const { data: serverConfig, isLoading, error } = useConfig();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const { toggleColorScheme } = useMantineColorScheme();

  // Get overrides from URL parameters (for customer embedding)
  // Use non-hook version since this provider is outside router context
  const urlColorScheme = getColorSchemeOverride();
  const urlWidth = getWidthOverride();
  const urlHeight = getHeightOverride();

  // Use server config directly or local config if it has been modified
  const currentConfig = config || serverConfig;

  // Create final config with URL parameter overrides applied
  const finalConfig = currentConfig
    ? {
        ...currentConfig,
        appearance: {
          ...currentConfig.appearance,
          // Override theme with URL parameter if provided
          theme: urlColorScheme || currentConfig.appearance.theme,
          // Override width with URL parameter if provided
          width: urlWidth !== null ? urlWidth : currentConfig.appearance.width,
          // Override height with URL parameter if provided
          height: urlHeight !== null ? urlHeight : currentConfig.appearance.height,
        },
      }
    : null;

  // Initialize Mantine color scheme from final config theme when config first loads
  useEffect(() => {
    if (finalConfig?.appearance?.theme) {
      // Always respect URL parameter color scheme, otherwise check localStorage
      if (urlColorScheme) {
        toggleColorScheme(urlColorScheme);
      } else {
        const storedColorScheme = localStorage.getItem("mantine-color-scheme");
        if (!storedColorScheme) {
          toggleColorScheme(finalConfig.appearance.theme);
        }
      }
    }
  }, [finalConfig?.appearance?.theme, urlColorScheme, toggleColorScheme]);

  const updateConfig = (updates: Partial<WidgetConfig>) => {
    if (!currentConfig) return;
    setConfig({
      ...currentConfig,
      ...updates,
    });
  };

  const updateFeatures = (updates: Partial<WidgetFeatures>) => {
    if (!currentConfig) return;
    setConfig({
      ...currentConfig,
      features: { ...currentConfig.features, ...updates },
    });
  };

  const updateAppearance = (updates: Partial<WidgetAppearance>) => {
    if (!currentConfig) return;

    setConfig({
      ...currentConfig,
      appearance: { ...currentConfig.appearance, ...updates },
    });
  };

  const toggleFeature = (feature: "portfolio" | "rewards" | "swap" | "defi" | "discover") => {
    if (!currentConfig) return;

    const currentValue = currentConfig.features[feature];

    // Compute proposed value preserving object form when present
    const proposedValue =
      typeof currentValue === "boolean"
        ? !currentValue
        : { ...currentValue, enabled: !currentValue.enabled };

    // Prevent disabling last enabled core feature
    const proposedFeatures = {
      ...currentConfig.features,
      [feature]: proposedValue,
    } as WidgetFeatures;
    const normalized = normalizeFeatures(proposedFeatures);
    const enabledCount = [
      normalized.portfolio,
      normalized.rewards,
      normalized.swap,
      normalized.defi,
      normalized.discover,
    ].filter(Boolean).length;
    if (enabledCount === 0) return;

    updateFeatures({ [feature]: proposedValue } as Partial<WidgetFeatures>);
  };

  const resetConfig = () => {
    // Reset to null and refetch from server
    setConfig(null);

    // Invalidate and refetch the query
    queryClient.invalidateQueries({ queryKey: ["config"] });
  };

  // Show loading state until config is loaded
  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  // // If there's an error, throw it
  if (error) {
    throw new Error("Failed to load widget config");
  }

  if (!finalConfig) {
    throw new Error("Widget config not found");
  }

  return (
    <WidgetConfigContext.Provider
      value={{
        config: finalConfig,
        updateConfig,
        updateFeatures,
        updateAppearance,
        toggleFeature,
        resetConfig,
        features: normalizeFeatures(finalConfig.features),
        appearance: finalConfig.appearance,
      }}
    >
      {children}
    </WidgetConfigContext.Provider>
  );
};

export const useWidgetConfig = () => {
  const context = useContext(WidgetConfigContext);
  if (context === undefined) {
    throw new Error("useWidgetConfig must be used within a WidgetConfigProvider");
  }
  return context;
};

export const useFeatures = () => {
  const { features, toggleFeature, updateFeatures } = useWidgetConfig();
  return { features, toggleFeature, updateFeatures };
};

export const useAppearance = () => {
  const { appearance, updateAppearance } = useWidgetConfig();
  const { toggleColorScheme } = useMantineColorScheme();

  // Enhanced updateAppearance to also sync with Mantine colorScheme
  const enhancedUpdateAppearance = (updates: Partial<WidgetAppearance>) => {
    updateAppearance(updates);

    // If theme is being updated, sync with Mantine
    if (updates.theme) {
      toggleColorScheme(updates.theme);
    }
  };

  return {
    appearance,
    updateAppearance: enhancedUpdateAppearance,
  };
};
